import { Box, Flex, useColorMode, useColorStyle } from '@tonic-one/react';
import PoseidonBasedEnableEmailAndCollaboration from './PoseidonBasedEnableEmailAndCollaboration';
import React, { useEffect, useState } from 'react';
import CSpinner from '@Components/CSpinner';
import { AccountApi, V1InstanceApi } from '@Apis';
import FoundationBasedEnableApiLayerProtection from './FoundationBasedEnableApiLayerProtection';
import FoundationBasedEnableEmailAndCollaboration from './FoundationBasedEnableEmailAndCollaboration';
import UpdateToVisionOne from './UpdateToVisionOne';
import { useTranslation } from 'react-i18next';
import PermissionDenied from './PermissionDenied';
import config from 'src/config';
import './index.less';
import Provisionning from './Provisionning';
import ServiceDenied from './ServiceDenied';
import { useNavigate } from 'react-router-dom';
import TrialActiveConfirmModal from './components/TrialActiveConfirmModal';
import TrialExpiredAndPurgedConfirmModal from './components/TrialExpiredAndPurgedConfirmModal';
import TrialExpiredAndLockConfirmModal from './components/TrialExpiredAndLockConfirmModal';
import common from '@Utils/common';
import {
  creditOneConsumptionFeatureShow,
  useUicPermissions,
} from '@Models/useUicPermissions';
import { useGeneralTrialStatus } from './hooks/useGeneralTrialStatus';

export enum Process {
  PoseidonBasedEnableEmailAndCollaboration = 'PoseidonBasedEnableEmailAndCollaboration',
  FoundationBasedEnableApiLayerProtection = 'FoundationBasedEnableApiLayerProtection',
  FoundationBasedEnableEmailAndCollaboration = 'FoundationBasedEnableEmailAndCollaboration',
  UpdateToVisionOne = 'UpdateToVisionOne',
  PermissionDenied = 'PermissionDenied',
  Provisioning = 'Provisioning',
  ServiceDenied = 'ServiceDenied',
}
export interface IsAdvancedInfo {
  isFoundation: boolean;
  isCredit: boolean;
  isMigrated: boolean;
  userType: string;
  isPaid: number;
  validCredit: number;
  view?: string;
}

export enum UpgradeStatus {
  NotUpgrade = 0,
  NewProvisionUpgrading = 12,
  Upgrading = 11,
  UpgradeDone = 21,
  NewProvisionUpgradeDone = 22,
  UpgradeFailed = -11,
  NewProvisionFail = -12,
}

/**
 * https://wiki.jarvis.trendmicro.com/display/TMCASP/%5BNew+IAM%5D+First+Stage+Detail+Design
 * @constructor
 */
const EmailAndCollaboration = () => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const [loading, setLoading] = useState(false);
  const [process, setProcess] = useState<Process>();
  const uicAdvancedInfo: IsAdvancedInfo =
    common.safeParseLocalStorage<IsAdvancedInfo>('uic_advancedInfo', {
      isFoundation: false,
      isCredit: false,
      isMigrated: false,
      userType: '',
      isPaid: 0,
      validCredit: 0,
    });
  const navigate = useNavigate();
  const [upgradeStatus, setUpgradeStatus] = useState(0);
  const [isProvisioned, setIsProvisioned] = useState(false);
  const [isValidLicense, setIsValidLicense] = useState(true);
  const [isWaitingForStatusUpdate, setIsWaitingForStatusUpdate] =
    useState(false);
  const { t } = useTranslation();
  const [isTrialActiveModalOpen, setIsTrialActiveModalOpen] = useState(false);
  const [
    isTrialExpiredAndPurgedModalOpen,
    setIsTrialExpiredAndPurgedModalOpen,
  ] = useState(false);
  const [isTrialExpiredAndLockModalOpen, setIsTrialExpiredAndLockModalOpen] =
    useState(false);
  const { data: permissionsData } = useUicPermissions();
  const isCreditOneConsumptionFeatureShow =
    creditOneConsumptionFeatureShow(permissionsData);
  const { data: isGeneralTrial } = useGeneralTrialStatus();

  useEffect(() => {
    (async function doCall() {
      try {
        setLoading(true);
        await getStepUpStatus();
      } catch (error: any) {
        console.log('error', error);
        const traceId = error?.data?.traceId;
        if (traceId) {
          navigate(`/service-exception?traceId=${traceId}`, { replace: true });
        } else {
          navigate('/service-exception', { replace: true });
        }
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Handle upgrade done status - user has completed upgrade process
   */
  const handleUpgradeDoneStatus = (isValidCasLicense: boolean) => {
    const isValidCredit = Boolean(uicAdvancedInfo.validCredit);

    if (!isValidCredit) {
      setIsWaitingForStatusUpdate(false);
      setProcess(Process.FoundationBasedEnableApiLayerProtection);
      return;
    }

    if (isValidCredit && !isValidCasLicense) {
      // Only click this button to consume paid credit then should wait license sync
      console.log(
        'isWaitingForStatusUpdate',
        isWaitingForStatusUpdate,
        isValidCredit,
        isValidCasLicense
      );
      if (isWaitingForStatusUpdate) {
        // Keep waiting for license to be activated, don't clear the flag
        setProcess(Process.Provisioning);
        return;
      }
      console.log('license is not active');
      setProcess(Process.FoundationBasedEnableEmailAndCollaboration);
      return;
    }

    // License is active - clear the waiting flag and navigate to dashboard
    console.log('license is active');
    setIsWaitingForStatusUpdate(false);
    sessionStorage.setItem('casIsUpgrade', 'true');
    const parentWindow = window.parent as unknown as UICParentWindow;
    if (parentWindow.APP?.$router?.push) {
      parentWindow.APP.$router.push(`${config.ui.uicPrefix}/dashboard`);
    } else {
      console.error('Parent router not available');
      navigate(`${config.ui.uicPrefix}/dashboard`, { replace: true });
    }
  };

  /**
   * Handle not upgraded status - check if need provision or allow upgrade
   */
  const handleNotUpgradedOrNotProvisionedStatus = async (
    clpCompanyId: string,
    isProvisioned: boolean
  ) => {
    if (!isProvisioned) {
      await foundationOrPoseidonBasedNoProvisioned();
      return;
    }

    const isNeedProvision: boolean = (
      await AccountApi.isNeedProvisionNew(clpCompanyId)
    ).data;
    console.log('isNeedProvision', isNeedProvision);

    if (isNeedProvision) {
      await foundationOrPoseidonBasedNoProvisioned();
      return;
    }

    const allowUpgrade: boolean = (
      await AccountApi.isAllowUpgradeV1ECS(clpCompanyId)
    ).data;

    if (!allowUpgrade) {
      setProcess(Process.ServiceDenied);
      return;
    }

    setProcess(Process.UpdateToVisionOne);
  };

  /**
   * Get step up status and determine which process to show
   */
  const getStepUpStatus = async () => {
    const clpCompanyId = common.getLocalStorageString('uic_companyGUID');
    if (!clpCompanyId) {
      return;
    }

    const v1StepUpStatus: {
      isProvisioned: boolean;
      status: number;
      licenseActive: boolean;
    } = (await AccountApi.getV1EASSetupStatus(clpCompanyId)).data;

    // Update state based on API response
    setUpgradeStatus(v1StepUpStatus.status);
    setIsProvisioned(v1StepUpStatus.isProvisioned);
    setIsValidLicense(v1StepUpStatus.licenseActive);

    // Handle different upgrade status
    if (
      [
        UpgradeStatus.NewProvisionUpgradeDone,
        UpgradeStatus.UpgradeDone,
      ].includes(v1StepUpStatus.status)
    ) {
      handleUpgradeDoneStatus(v1StepUpStatus.licenseActive);
    } else if (
      [UpgradeStatus.NewProvisionUpgrading].includes(v1StepUpStatus.status)
    ) {
      // Clear the waiting flag when backend confirms it's upgrading
      setIsWaitingForStatusUpdate(false);
      setProcess(Process.Provisioning);
    } else if ([UpgradeStatus.Upgrading].includes(v1StepUpStatus.status)) {
      setProcess(Process.UpdateToVisionOne);
    } else {
      // If we're waiting for status update, don't change the process
      // Just wait for backend to update the status
      if (isWaitingForStatusUpdate) {
        console.log('Still waiting for backend to update status...');
        return;
      }

      await handleNotUpgradedOrNotProvisionedStatus(
        clpCompanyId,
        v1StepUpStatus.isProvisioned
      );
    }
  };

  async function foundationOrPoseidonBasedNoProvisioned() {
    const isFoundation = uicAdvancedInfo.isFoundation;
    if (!isFoundation) {
      setProcess(Process.PoseidonBasedEnableEmailAndCollaboration);
    } else {
      if (uicAdvancedInfo.isCredit) {
        setProcess(Process.FoundationBasedEnableEmailAndCollaboration);
      } else {
        const clpCompanyId = common.getLocalStorageString('uic_companyGUID');
        const allowUpgrade: boolean = (
          await AccountApi.isAllowUpgradeV1ECS(clpCompanyId)
        ).data;
        if (!allowUpgrade) {
          setProcess(Process.ServiceDenied);
          return;
        }
        setProcess(Process.FoundationBasedEnableApiLayerProtection);
      }
    }
  }

  async function loadingStepUpStatus(timer: any) {
    if (
      ![
        UpgradeStatus.Upgrading,
        UpgradeStatus.NewProvisionUpgrading,
        UpgradeStatus.NotUpgrade,
      ].includes(upgradeStatus) &&
      isValidLicense
    ) {
      setLoading(false);
      clearInterval(timer);
    }
    await getStepUpStatus();
  }

  const actualProvisionNewCas = async () => {
    const previousProcess = process; // Save current page to return to if error occurs
    try {
      setLoading(true);

      // Set flag to indicate we're waiting for status update
      setIsWaitingForStatusUpdate(true);

      // Set status to NewProvisionUpgrading to prevent polling from stopping immediately
      // (will be updated by getStepUpStatus when backend responds)
      setUpgradeStatus(UpgradeStatus.NewProvisionUpgrading);

      // Immediately proceed to provisioning page
      setProcess(Process.Provisioning);

      // Fire and forget the API call, but handle errors separately
      V1InstanceApi.createCasInstance().catch((error: any) => {
        console.error('Failed to create CAS instance:', error);
        // Clear the waiting flag when API call fails
        setIsWaitingForStatusUpdate(false);

        if (error.status === 403) {
          // 403 error -> go to PermissionDenied page
          setProcess(Process.PermissionDenied);
        } else {
          // Other errors -> return to previous page and show error notification
          setProcess(previousProcess);
          window.showNotification({
            titleMsg: t('common.apiException.internalError'),
            severity: 'error',
          });
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const provisionNewCas = async () => {
    // Check trial status to determine which modal to show
    try {
      if (!isCreditOneConsumptionFeatureShow) {
        await actualProvisionNewCas();
        return;
      }

      // First, check if user is in general trial (stockType === "Complimentary")
      // Use cached data from hook instead of making API call
      if (isGeneralTrial) {
        // Case 2: General trial - no modal, provision directly
        console.log('User is in general trial, provisioning directly...');
        await actualProvisionNewCas();
        return;
      }

      // Then check solution trial status
      const response = await V1InstanceApi.getSolutionTrialEligibilityStatus();
      const { latestTrial } = response;
      console.log('latestTrial', latestTrial);
      // Case 1: User is in active solution trial - show TrialActiveConfirmModal
      if (latestTrial?.status === 'Activated') {
        console.log(`User is in active solution trial`);
        setIsTrialActiveModalOpen(true);
        return;
      }

      console.log(
        `isValidLicense=${isValidLicense},uicAdvancedInfo.validCredit=${uicAdvancedInfo.validCredit},upgradeStatus=${upgradeStatus}`
      );
      if (uicAdvancedInfo.validCredit === 1) {
        if (!isValidLicense && isProvisioned) {
          // Case 4: Lock period (instance exists, in upgrade done status)
          console.log(
            'Case 4: Lock period - show TrialExpiredAndLockConfirmModal'
          );
          setIsTrialExpiredAndLockModalOpen(true);
          return;
        } else {
          // Case 3: never started trial,start to consume paid credit directly
          console.log(
            'Valid license with paid credit - show TrialExpiredAndPurgedModalOpen'
          );
          setIsTrialExpiredAndPurgedModalOpen(true);
          return;
        }
      }
    } catch (error) {
      console.error('Failed to check trial eligibility:', error);
      window.showNotification({
        titleMsg: t('common.apiException.internalError'),
        severity: 'error',
      });
    }
  };

  const handleConfirmTrialActiveProvision = async () => {
    setIsTrialActiveModalOpen(false);
    try {
      setLoading(true);
      // Call Skip Solution Trial API
      await V1InstanceApi.skipSolutionTrial();
      console.log('Solution trial skipped successfully');
      // Then proceed with provision
      await actualProvisionNewCas();
    } catch (error) {
      console.error('Failed to skip solution trial:', error);
      window.showNotification({
        titleMsg: t('common.apiException.internalError'),
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // Case 3: Purged or never started trial
  const handleConfirmTrialExpiredAndPurgedProvision = async () => {
    setIsTrialExpiredAndPurgedModalOpen(false);
    try {
      setLoading(true);
      // Call Skip Solution Trial API
      await V1InstanceApi.skipSolutionTrial();
      console.log('Solution trial skipped successfully - Case 3 (Purged)');
      // Execute provision
      await actualProvisionNewCas();
    } catch (error) {
      console.error('Failed to skip solution trial (Case 3):', error);
      window.showNotification({
        titleMsg: t('common.apiException.internalError'),
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // Case 4: Lock period (instance exists)
  const handleConfirmTrialExpiredAndLockProvision = async () => {
    setIsTrialExpiredAndLockModalOpen(false);
    setIsWaitingForStatusUpdate(true);
    try {
      setLoading(true);
      // Call Skip Solution Trial API
      await V1InstanceApi.skipSolutionTrial();
      console.log('Solution trial skipped successfully - Case 4 (Lock period)');
      // Re-fetch the latest status to check if still in provision (21/22)
      // getStepUpStatus will:
      // - If still in 21/22 and isWaitingForStatusUpdate = true, enter Provisioning
      // - Otherwise, handle according to the new status
      if (
        [
          UpgradeStatus.NewProvisionUpgradeDone,
          UpgradeStatus.UpgradeDone,
        ].includes(upgradeStatus)
      ) {
        setLoading(false);
        setProcess(Process.Provisioning);
      } else {
        await actualProvisionNewCas();
      }
    } catch (error) {
      console.error('Failed to skip solution trial (Case 4):', error);
      window.showNotification({
        titleMsg: t('common.apiException.internalError'),
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelProvision = () => {
    setIsTrialActiveModalOpen(false);
    setIsTrialExpiredAndPurgedModalOpen(false);
    setIsTrialExpiredAndLockModalOpen(false);
  };

  return (
    <Box fontFamily="Segoe UI, -apple-system, BlinkMacSystemFont, Helvetica Neue, Helvetica, Arial, sans-serif">
      {loading && <CSpinner />}
      <Box className="welcomeSteps" minWidth={1524}>
        <Flex
          justifyContent="center"
          alignItems="flex-start"
          minHeight="100vh"
          width="100vw"
          backgroundColor={colorStyle.background.primary}
        >
          {process === Process.UpdateToVisionOne && (
            <UpdateToVisionOne
              setLoading={setLoading}
              loadingStepUpStatus={loadingStepUpStatus}
              isUpgradeLoading={upgradeStatus === UpgradeStatus.Upgrading}
              isUpgradeDone={upgradeStatus === UpgradeStatus.UpgradeDone}
            />
          )}
          {process === Process.PoseidonBasedEnableEmailAndCollaboration && (
            <PoseidonBasedEnableEmailAndCollaboration
              isFoundation={uicAdvancedInfo.isFoundation}
              provisionNewCas={provisionNewCas}
              setLoading={setLoading}
            />
          )}
          {process === Process.FoundationBasedEnableApiLayerProtection && (
            <FoundationBasedEnableApiLayerProtection />
          )}
          {process === Process.FoundationBasedEnableEmailAndCollaboration && (
            <FoundationBasedEnableEmailAndCollaboration
              setLoading={setLoading}
              provisionNewCas={provisionNewCas}
              actualProvisionNewCas={actualProvisionNewCas}
              isCreditOneConsumptionFeatureShow={
                isCreditOneConsumptionFeatureShow
              }
            />
          )}
          {process === Process.PermissionDenied && <PermissionDenied />}
          {process === Process.Provisioning && (
            <Provisionning
              loadingStepUpStatus={loadingStepUpStatus}
              upgradeStatus={upgradeStatus}
            />
          )}
          {process === Process.ServiceDenied && <ServiceDenied />}
        </Flex>
      </Box>
      <TrialActiveConfirmModal
        isOpen={isTrialActiveModalOpen}
        onCancel={handleCancelProvision}
        onConfirm={handleConfirmTrialActiveProvision}
      />
      <TrialExpiredAndPurgedConfirmModal
        isOpen={isTrialExpiredAndPurgedModalOpen}
        onCancel={handleCancelProvision}
        onConfirm={handleConfirmTrialExpiredAndPurgedProvision}
      />
      <TrialExpiredAndLockConfirmModal
        isOpen={isTrialExpiredAndLockModalOpen}
        onCancel={handleCancelProvision}
        onConfirm={handleConfirmTrialExpiredAndLockProvision}
      />
    </Box>
  );
};
export default EmailAndCollaboration;
